import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Form, message } from 'antd';
import { useInviteUserMutation } from 'gql/types';
import { Alert, Button, FormItem, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { InviteFields } from './enum';
import { fields } from './fields';
import { InviteSchema, inviteSchema } from './schema';
import styles from './styles.module.scss';

const InviteForm: FC = () => {
  const { t } = useTranslation('admin.InvitePage');
  const navigate = useNavigate();

  const defaultFields = Object.values(InviteFields).reduce(
    (fields, fieldName) => {
      return { ...fields, [fieldName]: '' };
    },
    {} as InviteSchema
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    getValues,
  } = useForm<InviteSchema>({
    resolver: yupResolver(inviteSchema),
    mode: 'onChange',
    defaultValues: {
      ...defaultFields,
    },
  });

  const [errorMessage, setErrorMessage] = useState<string>();

  const [inviteUser, inviteUserMutation] = useInviteUserMutation({
    onError: ({ message: m }) => {
      setErrorMessage(m);
    },
    onCompleted: (result) => {
      if (result?.inviteUser) {
        message.success(t('success'));
        reset();
        removeErrorMessage();
      }
    },
  });

  const removeErrorMessage = () => setErrorMessage(undefined);

  const submitFormData = async (formData: InviteSchema) => {
    inviteUser({
      variables: {
        input: {
          ...formData,
        },
      },
    });
  };

  const inviteHandler = handleSubmit(submitFormData);

  const handleSave = async () => {
    const formData = getValues();
    await submitFormData(formData);
  };

  const closeHandler = () => {
    navigate(-1);
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  return (
    <>
      <Spin spinning={inviteUserMutation.loading}>
        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            closable
            afterClose={removeErrorMessage}
          />
        )}
        <Form layout="vertical">
          {fields.map(({ label, name, Component }) => {
            const error = errors[name]?.message;
            return (
              <FormItem
                key={name}
                label={label}
                extra={error}
                validateStatus={error && 'error'}
              >
                <Component name={name} control={control} />
              </FormItem>
            );
          })}
          <div className={styles.buttonsContainer}>
            <Button onClick={closeHandler}>{t('close')}</Button>

            <Button disabled={!isValid} onClick={inviteHandler} type="primary">
              {t('invite')}
            </Button>
          </div>
        </Form>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={inviteUserMutation.loading}
      />
    </>
  );
};

export default InviteForm;
